import React, { useState, useEffect, useCallback } from 'react';
import { FaFileAlt, FaCloudUploadAlt, FaEye, FaTrash, FaCheck, FaCogs, FaDownload, FaCalendarAlt, FaFilePdf, FaInfoCircle, FaLink, FaCopy, FaExternalLinkAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import usePreviredPeriodo from '../../hooks/usePreviredPeriodo';
import * as previredService from '../../services/previredService';
import '../../styles/pages/RRHH/PreviredPage.css';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatCLP = (val) => `$${parseInt(val || 0).toLocaleString('es-CL')}`;

const PreviredPage = ({ token, id_company }) => {
    const currentDate = new Date();
    const {
        mes, setMes, anio, setAnio,
        estado, loading, error,
        descargarTxt, descargando,
        subirPdf, subiendo,
        verDetalle, cerrarDetalle, detalle,
        eliminarPdf, cambiarEstadoPdf, reprocesar,
    } = usePreviredPeriodo(id_company, currentDate.getMonth() + 1, currentDate.getFullYear());

    const [pdfFile, setPdfFile] = useState(null);
    const [pdfAdicionalFile, setPdfAdicionalFile] = useState(null);
    const [activeTab, setActiveTab] = useState('AFP');

    // Estado link de pago Previred
    const [pagoPrevired, setPagoPrevired] = useState(null);
    const [generandoLink, setGenerandoLink] = useState(false);
    const [confirmandoPago, setConfirmandoPago] = useState(false);

    const fetchPagoPrevired = useCallback(async () => {
        try {
            const data = await previredService.getEstadoPagoPrevired(id_company, mes, anio);
            setPagoPrevired(data.existe ? data : null);
        } catch {
            setPagoPrevired(null);
        }
    }, [id_company, mes, anio]);

    useEffect(() => {
        fetchPagoPrevired();
    }, [fetchPagoPrevired]);

    const pdf = estado?.pdf_cargado;

    // --- Handlers ---

    const handleDescargarTxt = async () => {
        try {
            Swal.fire({ title: 'Generando...', html: 'Preparando archivo TXT', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await descargarTxt();
            Swal.fire('Descargado', `Archivo previred_${String(mes).padStart(2, '0')}_${anio}.txt generado`, 'success');
        } catch (err) {
            Swal.fire('Error', err.message || 'Error al generar archivo', 'error');
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
        } else if (file) {
            Swal.fire('Error', 'Solo se permiten archivos PDF', 'error');
            e.target.value = '';
        }
    };

    const handleFileAdicionalChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfAdicionalFile(file);
        } else if (file) {
            Swal.fire('Error', 'Solo se permiten archivos PDF', 'error');
            e.target.value = '';
        }
    };

    const handleSubirPdfAdicional = async () => {
        if (!pdfAdicionalFile) return;
        const confirm = await Swal.fire({
            title: 'Cargar PDF adicional',
            html: `<p>Se sumaran las cotizaciones de este PDF a las ya existentes en el período <strong>${MESES[mes - 1]} ${anio}</strong>.</p><p>Los trabajadores repetidos tendrán sus montos acumulados.</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, cargar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2bc046'
        });
        if (!confirm.isConfirmed) return;
        try {
            Swal.fire({ title: 'Procesando PDF...', html: 'Extrayendo datos con IA', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const result = await subirPdf(pdfAdicionalFile, true);
            const prop = result.propagacion?.resultados || {};
            Swal.fire({
                title: 'PDF Adicional Procesado',
                html: `
                    <p><strong>Trabajadores procesados:</strong> ${prop.procesados || 0}</p>
                    <p><strong>Liquidaciones creadas:</strong> ${prop.liquidacionesCreadas || 0}</p>
                    <p><strong>Liquidaciones actualizadas:</strong> ${prop.liquidacionesActualizadas || 0}</p>
                    <p><strong>Registros insertados:</strong> ${result.data?.detalles_insertados || 0}</p>
                `,
                icon: 'success'
            });
            setPdfAdicionalFile(null);
            const input = document.getElementById('pdf-adicional-input');
            if (input) input.value = '';
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.response?.data?.message || 'Error al procesar el PDF', 'error');
        }
    };

    const handleSubirPdf = async () => {
        if (!pdfFile) return;
        try {
            Swal.fire({ title: 'Procesando PDF...', html: 'Extrayendo datos con IA', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const result = await subirPdf(pdfFile);
            Swal.fire({
                title: 'PDF Procesado',
                html: `
                    <p><strong>Trabajadores:</strong> ${result.data?.resumen?.total_trabajadores || 0}</p>
                    <p><strong>Total Remuneraciones:</strong> ${formatCLP(result.data?.resumen?.total_remuneraciones)}</p>
                    <p><strong>Registros insertados:</strong> ${result.data?.detalles_insertados || 0}</p>
                `,
                icon: 'success'
            });
            setPdfFile(null);
            const input = document.getElementById('pdf-input');
            if (input) input.value = '';
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.response?.data?.message || 'Error al procesar el PDF', 'error');
        }
    };

    const handleVerDetalle = async (id) => {
        try {
            await verDetalle(id);
            setActiveTab('AFP');
        } catch {
            Swal.fire('Error', 'No se pudo cargar el detalle', 'error');
        }
    };

    const handleEliminar = async (id) => {
        const result = await Swal.fire({
            title: 'Eliminar registro?', text: 'Esta accion no se puede deshacer',
            icon: 'warning', showCancelButton: true, confirmButtonText: 'Si, eliminar', cancelButtonText: 'Cancelar'
        });
        if (result.isConfirmed) {
            try {
                await eliminarPdf(id);
                Swal.fire('Eliminado', 'Registro eliminado correctamente', 'success');
            } catch {
                Swal.fire('Error', 'No se pudo eliminar el registro', 'error');
            }
        }
    };

    const handleReprocesar = async (id) => {
        let mesSig = mes + 1, anioSig = anio;
        if (mesSig > 12) { mesSig = 1; anioSig = anio + 1; }

        const confirm = await Swal.fire({
            title: 'Procesar Asientos Contables',
            html: `
                <ul style="text-align: left; margin-left: 20px;">
                    <li>Crear/actualizar liquidaciones de sueldo</li>
                    <li><strong>Devengado:</strong> ${MESES[mes - 1]} ${anio}</li>
                    <li><strong>Pago Remuneraciones:</strong> 5 de ${MESES[mesSig - 1]} ${anioSig}</li>
                    <li><strong>Pago Previred:</strong> 13 de ${MESES[mesSig - 1]} ${anioSig}</li>
                </ul>
            `,
            icon: 'question', showCancelButton: true, confirmButtonText: 'Si, procesar',
            cancelButtonText: 'Cancelar', confirmButtonColor: '#2bc046'
        });

        if (confirm.isConfirmed) {
            try {
                Swal.fire({ title: 'Procesando...', html: 'Generando liquidaciones y asientos contables', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                await reprocesar(id);
                Swal.fire({
                    title: 'Asientos procesados',
                    icon: 'success'
                });
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Error al procesar', 'error');
            }
        }
    };

    const handleGenerarLinkPago = async () => {
        setGenerandoLink(true);
        try {
            Swal.fire({ title: 'Calculando monto...', html: 'Sumando cotizaciones del período', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const data = await previredService.generarLinkPagoPrevired(id_company, mes, anio);
            Swal.close();
            setPagoPrevired({ existe: true, ...data });
            window.open(data.link_pago, '_blank');
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Error al generar link de pago', 'error');
        } finally {
            setGenerandoLink(false);
        }
    };

    const handleCopiarLinkPago = () => {
        if (pagoPrevired?.link_pago) {
            navigator.clipboard.writeText(pagoPrevired.link_pago);
            Swal.fire({ icon: 'success', title: 'Link copiado', timer: 1200, showConfirmButton: false });
        }
    };

    const handleConfirmarPagoPrevired = async () => {
        if (!pagoPrevired?.codigo_pago) return;
        setConfirmandoPago(true);
        try {
            Swal.fire({ title: 'Confirmando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await previredService.confirmarPagoPrevired(
                pagoPrevired.codigo_pago,
                `Pago Previred confirmado manualmente para ${MESES[mes - 1]} ${anio}`
            );
            Swal.fire('Confirmado', 'Pago Previred confirmado exitosamente', 'success');
            await fetchPagoPrevired();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Error al confirmar pago', 'error');
        } finally {
            setConfirmandoPago(false);
        }
    };

    const handleCambiarEstado = async (id, nuevoEstado) => {
        try {
            await cambiarEstadoPdf(id, nuevoEstado);
            Swal.fire('Exito', `Estado cambiado a ${nuevoEstado}`, 'success');
        } catch {
            Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
        }
    };

    // --- Render ---

    return (
        <div className="previred-container">
            {/* Header */}
            <div className="previred-header">
                <div>
                    <h1><FaFileAlt /> Previred</h1>
                    <p>Declaraciones mensuales de cotizaciones previsionales</p>
                </div>
            </div>

            {/* Paso 1: Selector de periodo */}
            <div className="periodo-selector">
                <div className="periodo-selector-label">
                    <FaCalendarAlt />
                    <span>Seleccionar periodo</span>
                </div>
                <div className="periodo-selector-controls">
                    <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}>
                        {MESES.map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                        ))}
                    </select>
                    <select value={anio} onChange={(e) => setAnio(parseInt(e.target.value))}>
                        {[2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Estado de carga */}
            {loading && <div className="loading">Cargando estado del periodo...</div>}
            {error && <div className="error-msg"><FaInfoCircle /> {error}</div>}

            {/* Panel de acciones del periodo */}
            {!loading && estado && (
                <div className="periodo-panel">
                    {/* Tarjeta: Descargar TXT */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaDownload />
                            <h3>Descargar TXT Previred</h3>
                        </div>
                        <div className="action-card-body">
                            {estado.tiene_liquidaciones ? (
                                <>
                                    <p className="action-info">{estado.cantidad_liquidaciones} liquidaciones encontradas para {MESES[mes - 1]} {anio}</p>
                                    <button className="btn-action btn-icon-tooltip btn-descargar" onClick={handleDescargarTxt} disabled={descargando} title={descargando ? 'Generando...' : 'Descargar TXT'}>
                                        <FaDownload /><span className="btn-tooltip-text">{descargando ? 'Generando...' : 'Descargar TXT'}</span>
                                    </button>
                                </>
                            ) : (
                                <p className="action-empty">No hay liquidaciones para este periodo. Debe crear las liquidaciones primero.</p>
                            )}
                        </div>
                    </div>

                    {/* Tarjeta: Link de Pago */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaLink />
                            <h3>Link de Pago</h3>
                        </div>
                        <div className="action-card-body">
                            {!pagoPrevired ? (
                                <>
                                    <p className="action-info">Genera el link de pago para que el cliente conozca el monto y adjunte el comprobante de transferencia.</p>
                                    <button
                                        className="btn-action btn-icon-tooltip btn-descargar"
                                        onClick={handleGenerarLinkPago}
                                        disabled={generandoLink || !estado?.tiene_liquidaciones}
                                        title={!estado?.tiene_liquidaciones ? 'Debe tener liquidaciones generadas' : 'Generar Link de Pago'}
                                    >
                                        <FaLink /><span className="btn-tooltip-text">Generar Link</span>
                                    </button>
                                    {!estado?.tiene_liquidaciones && (
                                        <p className="action-empty">Debe generar las liquidaciones del período primero.</p>
                                    )}
                                </>
                            ) : (
                                <div className="pdf-cargado">
                                    <div className="pdf-cargado-info">
                                        <span className={`badge badge-${pagoPrevired.estado?.toLowerCase()}`}>{pagoPrevired.estado}</span>
                                        <p><strong>Monto:</strong> ${parseInt(pagoPrevired.monto || 0).toLocaleString('es-CL')}</p>
                                        {pagoPrevired.comprobante_url && (
                                            <p>
                                                <a href={pagoPrevired.comprobante_url} target="_blank" rel="noopener noreferrer" className="btn-link">
                                                    📄 Ver comprobante
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                    <div className="pdf-cargado-actions">
                                        <button className="btn-action btn-icon-tooltip btn-ver-detalle" onClick={() => window.open(pagoPrevired.link_pago, '_blank')} title="Abrir Link">
                                            <FaExternalLinkAlt /><span className="btn-tooltip-text">Abrir Link</span>
                                        </button>
                                        <button className="btn-action btn-icon-tooltip btn-procesar" onClick={handleCopiarLinkPago} title="Copiar Link">
                                            <FaCopy /><span className="btn-tooltip-text">Copiar</span>
                                        </button>
                                        {pagoPrevired.estado === 'confirmado' ? (
                                            <span className="badge badge-procesado">✅ Pagado</span>
                                        ) : (
                                            <button
                                                className="btn-action btn-icon-tooltip btn-validar"
                                                onClick={handleConfirmarPagoPrevired}
                                                disabled={confirmandoPago}
                                                title="Confirmar Pago"
                                            >
                                                <FaCheck /><span className="btn-tooltip-text">Confirmar</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tarjeta: Cargar PDF pagado */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaFilePdf />
                            <h3>Certificado PDF Pagado</h3>
                        </div>
                        <div className="action-card-body">
                            {!pdf ? (
                                <div className="upload-zone">
                                    <p className="action-info">Suba el certificado Previred pagado para extraer datos con IA</p>
                                    <div className="upload-form">
                                        <input
                                            type="file"
                                            id="pdf-input"
                                            accept=".pdf"
                                            onChange={handleFileChange}
                                            disabled={subiendo}
                                        />
                                        {pdfFile && <span className="file-name">{pdfFile.name}</span>}
                                        <button
                                            className="btn-action btn-icon-tooltip btn-upload"
                                            onClick={handleSubirPdf}
                                            disabled={!pdfFile || subiendo}
                                            title={subiendo ? 'Procesando...' : 'Subir y Extraer'}
                                        >
                                            <FaCloudUploadAlt /><span className="btn-tooltip-text">{subiendo ? 'Procesando...' : 'Subir y Extraer'}</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="pdf-cargado">
                                        <div className="pdf-cargado-info">
                                            <span className={`badge badge-${pdf.estado?.toLowerCase()}`}>{pdf.estado}</span>
                                            <p><strong>Archivo:</strong> {pdf.nombre_archivo}</p>
                                            <p><strong>Empleador:</strong> {pdf.empleador_razon_social}</p>
                                            <p><strong>Trabajadores:</strong> {pdf.total_trabajadores}</p>
                                            <p><strong>Total Rem.:</strong> {formatCLP(pdf.total_remuneraciones)}</p>
                                        </div>
                                        <div className="pdf-cargado-actions">
                                            <button className="btn-action btn-icon-tooltip btn-ver-detalle" onClick={() => handleVerDetalle(pdf.id)} title="Ver Detalle">
                                                <FaEye /><span className="btn-tooltip-text">Ver Detalle</span>
                                            </button>
                                            {pdf.estado !== 'PROCESADO' && (
                                                <button className="btn-action btn-icon-tooltip btn-procesar" onClick={() => handleReprocesar(pdf.id)} title="Procesar Asientos">
                                                    <FaCogs /><span className="btn-tooltip-text">Procesar Asientos</span>
                                                </button>
                                            )}
                                            {pdf.estado === 'PENDIENTE' && (
                                                <button className="btn-action btn-icon-tooltip btn-validar" onClick={() => handleCambiarEstado(pdf.id, 'VALIDADO')} title="Validar">
                                                    <FaCheck /><span className="btn-tooltip-text">Validar</span>
                                                </button>
                                            )}
                                            <button className="btn-action btn-icon-tooltip btn-eliminar" onClick={() => handleEliminar(pdf.id)} title="Eliminar">
                                                <FaTrash /><span className="btn-tooltip-text">Eliminar</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="upload-zone upload-zone-adicional">
                                        <p className="action-info">¿Hay un pago adicional de Previred para este período? Carga otro PDF y sus cotizaciones se acumularán.</p>
                                        <div className="upload-form">
                                            <input
                                                type="file"
                                                id="pdf-adicional-input"
                                                accept=".pdf"
                                                onChange={handleFileAdicionalChange}
                                                disabled={subiendo}
                                            />
                                            {pdfAdicionalFile && <span className="file-name">{pdfAdicionalFile.name}</span>}
                                            <button
                                                className="btn-action btn-icon-tooltip btn-upload"
                                                onClick={handleSubirPdfAdicional}
                                                disabled={!pdfAdicionalFile || subiendo}
                                                title={subiendo ? 'Procesando...' : 'Subir PDF adicional'}
                                            >
                                                <FaCloudUploadAlt /><span className="btn-tooltip-text">{subiendo ? 'Procesando...' : 'Subir PDF adicional'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de detalle */}
            {detalle && (
                <div className="modal-overlay" onClick={cerrarDetalle}>
                    <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Detalle - {detalle.cabecera?.periodo_mes}/{detalle.cabecera?.periodo_anio}</h2>
                        </div>

                        <div className="modal-info">
                            <p><strong>Empleador:</strong> {detalle.cabecera?.empleador_razon_social}</p>
                            <p><strong>RUT:</strong> {detalle.cabecera?.empleador_rut}</p>
                            <p><strong>AFPs:</strong> {detalle.afps?.join(', ')}</p>
                        </div>

                        <div className="modal-tabs">
                            {['AFP', 'ISL', 'SALUD', 'SEGURO_SOCIAL', 'CAJA_COMPENSACION'].map(tab => (
                                <button
                                    key={tab}
                                    className={activeTab === tab ? 'active' : ''}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab} ({detalle.detalles?.[tab]?.length || 0})
                                </button>
                            ))}
                        </div>

                        <div className="modal-body">
                            <table className="detalle-table">
                                <thead>
                                    <tr>
                                        <th>RUT</th>
                                        <th>Nombre</th>
                                        <th>Institucion</th>
                                        <th>Remuneracion</th>
                                        {activeTab === 'AFP' && <th>Cot. Obligatoria</th>}
                                        {activeTab === 'AFP' && <th>SIS</th>}
                                        {activeTab === 'AFP' && <th>Cesantia Afil.</th>}
                                        {activeTab === 'AFP' && <th>Cesantia Empl.</th>}
                                        {activeTab === 'ISL' && <th>Tasa</th>}
                                        {activeTab === 'ISL' && <th>Cotizacion ISL</th>}
                                        {activeTab === 'SALUD' && <th>Entidad</th>}
                                        {activeTab === 'SALUD' && <th>Cotizacion 7%</th>}
                                        {activeTab === 'SALUD' && <th>Cot. Adicional Isapre</th>}
                                        {activeTab === 'SEGURO_SOCIAL' && <th>Seg. Social Previsional</th>}
                                        {activeTab === 'CAJA_COMPENSACION' && <th>Caja de Compensación</th>}
                                        {activeTab === 'CAJA_COMPENSACION' && <th>Cotiz. 4.5%</th>}
                                        <th>Mov.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detalle.detalles?.[activeTab]?.map((det, idx) => (
                                        <tr key={idx}>
                                            <td>{det.rut_trabajador}</td>
                                            <td>{det.nombre_trabajador}</td>
                                            <td>{det.nombre_institucion}</td>
                                            <td>{formatCLP(det.remuneracion)}</td>
                                            {activeTab === 'AFP' && <td>{formatCLP(det.cotizacion_obligatoria)}</td>}
                                            {activeTab === 'AFP' && <td>{formatCLP(det.sis)}</td>}
                                            {activeTab === 'AFP' && <td>{formatCLP(det.cotizacion_afiliado_cesantia)}</td>}
                                            {activeTab === 'AFP' && <td>{formatCLP(det.cotizacion_empleador_cesantia)}</td>}
                                            {activeTab === 'ISL' && <td>{det.tasa_cotizacion_isl}%</td>}
                                            {activeTab === 'ISL' && <td>{formatCLP(det.cotizacion_isl)}</td>}
                                            {activeTab === 'SALUD' && <td>{det.entidad_salud}</td>}
                                            {activeTab === 'SALUD' && <td>{formatCLP(det.cotizacion_salud_7pct)}</td>}
                                            {activeTab === 'SALUD' && <td>{formatCLP(det.cotizacion_adicional_isapre)}</td>}
                                            {activeTab === 'SEGURO_SOCIAL' && <td>{formatCLP(det.seguro_social_previsional)}</td>}
                                            {activeTab === 'CAJA_COMPENSACION' && <td>{det.nombre_institucion}</td>}
                                            {activeTab === 'CAJA_COMPENSACION' && <td>{formatCLP(det.cotizacion_caja_compensacion)}</td>}
                                            <td>{det.codigo_movimiento}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={cerrarDetalle}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PreviredPage;
